/**
 * Settled-state recap card — per Match spec §5 (Q32-folded).
 *
 * Surfaces when match.state === 'ft' (or 'settled' once that
 * transition is wired). Composition:
 *
 *   1. Hero matchup with final score (replaces pre-match score row)
 *   2. Verdict block — won/lost/no-call (equal visual prominence)
 *   3. Hypothetical P&L block — computed from cited line × price ×
 *      default stake (D32: NEVER implies bookmaker account visibility)
 *   4. Locked record of original reasoning (§5.5 — immutable, full
 *      A3 schema preserved frozen at moment of recommendation)
 *   5. Persona post-mortem (§5.6 — own-the-verdict mode)
 *
 * Anti-patterns rejected at component design (B6):
 *   - No apologetic copy ("Sorry, this one didn't land")
 *   - No defensive framing ("we knew this was risky")
 *   - No revisionist Monday-morning-quarterbacking
 *   - No win-state hero treatment that the loss-state lacks
 *   - No averaging-away ("Sharp8's overall record is still strong")
 */

import type { EdgeCall, Match, Persona, MatchScore } from '@sharp8/types';

interface Props {
  match: Match;
  edge: EdgeCall | null;        // null when match settled with no Sharp8 call
  personas: Persona[];
  defaultStake?: number;        // for hypothetical P&L — defaults to 100
}

interface VerdictResult {
  outcome: 'won' | 'lost' | 'pushed';
  marginText: string;           // 'by 2 goals' / 'by 0.5 on the line' / 'missed by ...'
}

const PERSONA_AVATARS: Record<string, { bg: string; fg: string; letter: string }> = {
  'sharp8-house': { bg: 'linear-gradient(135deg,#4ED5DB,#2EA8AE)', fg: '#0A1322', letter: 'S' },
  'tanaka':       { bg: 'linear-gradient(135deg,#1A3A6B,#0E1F3D)', fg: '#fff', letter: 'T' },
  'sofia':        { bg: 'linear-gradient(135deg,#FF6B9D,#C92A6F)', fg: '#fff', letter: 'S' },
  'arjun':        { bg: 'linear-gradient(135deg,#2D6B47,#1A4A30)', fg: '#fff', letter: 'A' },
  'caleb':        { bg: 'linear-gradient(135deg,#D4831F,#8B5A0C)', fg: '#fff', letter: 'C' },
  'lucas':        { bg: 'linear-gradient(135deg,#6B47B8,#422D7A)', fg: '#fff', letter: 'L' },
};

// ── Verdict derivation ────────────────────────────────────────────

/**
 * Outcome (won/lost/pushed) is read from the backend's settled
 * `edge.outcome` whenever the backend has resolved it. Pre-migration
 * edge_calls (and any rows still on outcome='pending' between FT and
 * the next cron settle cycle) fall back to an on-the-fly client-side
 * derivation so the surface doesn't go blank during the transition.
 *
 * The margin phrase ("by 2 goals", "pushed on the line") is always
 * derived client-side — the backend doesn't store it.
 *
 * Only Asian Handicap is fully implemented. Other markets return
 * null and the UI shows the UnsupportedMarketVerdict block.
 */
function deriveVerdict(args: {
  edge: EdgeCall;
  homeShortName: string;
  awayShortName: string;
  score: MatchScore;
  htScore: { home: number; away: number } | null;
  corners: { home: number; away: number } | null;
  yellows: { home: number; away: number } | null;
  reds:    { home: number; away: number } | null;
}): VerdictResult | null {
  const { edge } = args;
  if (edge.market === 'AH')      return deriveAHVerdict(args);
  if (edge.market === 'OU')      return deriveOUVerdict(args);
  if (edge.market === '1X2')     return derive1X2Verdict(args);
  if (edge.market === 'BTTS')    return deriveBTTSVerdict(args);
  if (edge.market === 'HTFT')    return deriveHTFTVerdict(args);
  if (edge.market === 'CORNERS') return deriveCornersVerdict(args);
  if (edge.market === 'CARDS')   return deriveCardsVerdict(args);
  return null;
}

/** Prefer backend outcome; derive client-side only during the
 *  FT → next cron-settle window. */
function resolveOutcome(
  backend: EdgeCall['outcome'],
  derivedAdjustedMargin: number,
): 'won' | 'lost' | 'pushed' {
  if (backend === 'won' || backend === 'lost' || backend === 'pushed') return backend;
  if (derivedAdjustedMargin >  0.001) return 'won';
  if (derivedAdjustedMargin < -0.001) return 'lost';
  return 'pushed';
}

function deriveAHVerdict(args: {
  edge: EdgeCall;
  homeShortName: string;
  awayShortName: string;
  score: MatchScore;
}): VerdictResult | null {
  const { edge, homeShortName, awayShortName, score } = args;

  // Parse 'Arsenal −0.75' / 'Liverpool +0.5' / 'Home -0.5' / 'Away +0.5'
  const m = edge.line.match(/^(\S.+?)\s+([−\-+])(\d+(\.\d+)?)\s*$/);
  if (!m) return null;
  const teamLabel = m[1]!;
  const sign = m[2] === '+' ? 1 : -1;
  const handicap = sign * Number(m[3]);

  // Determine which side this line is on (home or away)
  const onHome =
    teamLabel.toLowerCase().includes(homeShortName.toLowerCase()) ||
    teamLabel.toLowerCase() === 'home';
  const onAway =
    teamLabel.toLowerCase().includes(awayShortName.toLowerCase()) ||
    teamLabel.toLowerCase() === 'away';
  if (!onHome && !onAway) return null;

  const sideGoals = onHome ? score.home : score.away;
  const oppGoals  = onHome ? score.away : score.home;
  const adjustedMargin = (sideGoals + handicap) - oppGoals;

  const outcome = resolveOutcome(edge.outcome, adjustedMargin);

  // Margin phrase reflects the actual scoreline, matched to outcome.
  const marginText =
    outcome === 'pushed' ? 'pushed on the line' :
    outcome === 'won'    ? marginPhrase(sideGoals - oppGoals) :
                           marginPhrase(oppGoals - sideGoals);

  return { outcome, marginText };
}

function derive1X2Verdict(args: {
  edge: EdgeCall;
  homeShortName: string;
  awayShortName: string;
  score: MatchScore;
}): VerdictResult | null {
  const { edge, homeShortName, awayShortName, score } = args;
  const norm = edge.line.trim().toLowerCase();

  let pickSide: 'home' | 'draw' | 'away' | null = null;
  if (norm === 'home' || norm === '1') pickSide = 'home';
  else if (norm === 'draw' || norm === 'x') pickSide = 'draw';
  else if (norm === 'away' || norm === '2') pickSide = 'away';
  else if (norm.includes(homeShortName.toLowerCase())) pickSide = 'home';
  else if (norm.includes(awayShortName.toLowerCase())) pickSide = 'away';
  if (!pickSide) return null;

  const actualSide: 'home' | 'draw' | 'away' =
    score.home > score.away ? 'home' :
    score.home < score.away ? 'away' :
                              'draw';

  // 1X2 has no push outcome in normal play — pickSide either matches
  // the result or it doesn't. Backend may still report 'pushed' for
  // a voided market, in which case we honor it.
  const adjustedMargin = pickSide === actualSide ? 1 : -1;
  const outcome = resolveOutcome(edge.outcome, adjustedMargin);

  const resultText =
    actualSide === 'draw' ? 'finished level' :
    actualSide === 'home' ? `${homeShortName} won` :
                            `${awayShortName} won`;

  const marginText =
    outcome === 'pushed' ? `${resultText} · pushed`  :
    outcome === 'won'    ? resultText :
                           resultText;

  return { outcome, marginText };
}

function deriveBTTSVerdict(args: {
  edge: EdgeCall;
  score: MatchScore;
}): VerdictResult | null {
  const { edge, score } = args;
  const norm = edge.line.trim().replace(/^btts\s+/i, '').toLowerCase();
  if (norm !== 'yes' && norm !== 'no') return null;

  const bothScored = score.home > 0 && score.away > 0;
  const pickYes = norm === 'yes';
  const pickHit = pickYes === bothScored;

  const outcome = resolveOutcome(edge.outcome, pickHit ? 1 : -1);

  const scorelineText = `${score.home}–${score.away}`;
  const marginText =
    outcome === 'pushed' ? `pushed at ${scorelineText}` :
    bothScored           ? `both scored · ${scorelineText}` :
                           `one side blanked · ${scorelineText}`;

  return { outcome, marginText };
}

function deriveOUStyleVerdict(args: {
  edge: EdgeCall;
  total: number | null;
  unitLabel: string;     // "goals" | "corners" | "cards"
}): VerdictResult | null {
  const { edge, total, unitLabel } = args;
  if (total == null) return null; // stat missing — show UnsupportedMarketVerdict

  const m = edge.line.match(/^(Over|Under)\s+(\d+(\.\d+)?)\s*$/i);
  if (!m) return null;
  const direction = m[1]!.toLowerCase() as 'over' | 'under';
  const threshold = Number(m[2]);
  const delta = total - threshold;
  const adjustedMargin = direction === 'over' ? delta : -delta;

  const outcome = resolveOutcome(edge.outcome, adjustedMargin);

  const marginText =
    outcome === 'pushed' ? `pushed at ${total} ${unitLabel}` :
    outcome === 'won'    ? `${total} ${unitLabel} · ${direction} ${threshold} hit` :
                           `${total} ${unitLabel} · ${direction} ${threshold} missed`;

  return { outcome, marginText };
}

function deriveCornersVerdict(args: {
  edge: EdgeCall;
  corners: { home: number; away: number } | null;
}): VerdictResult | null {
  const total = args.corners ? args.corners.home + args.corners.away : null;
  return deriveOUStyleVerdict({ edge: args.edge, total, unitLabel: 'corners' });
}

function deriveCardsVerdict(args: {
  edge: EdgeCall;
  yellows: { home: number; away: number } | null;
  reds:    { home: number; away: number } | null;
}): VerdictResult | null {
  if (!args.yellows || !args.reds) {
    return deriveOUStyleVerdict({ edge: args.edge, total: null, unitLabel: 'cards' });
  }
  const total = args.yellows.home + args.yellows.away + args.reds.home + args.reds.away;
  return deriveOUStyleVerdict({ edge: args.edge, total, unitLabel: 'cards' });
}

function deriveHTFTVerdict(args: {
  edge: EdgeCall;
  score: MatchScore;
  htScore: { home: number; away: number } | null;
}): VerdictResult | null {
  const { edge, score, htScore } = args;
  if (!htScore) return null; // HT score missing — show UnsupportedMarketVerdict

  const norm = edge.line.trim().toLowerCase().replace(/\s+/g, '');
  const tokenToSide = (t: string): 'home' | 'draw' | 'away' | null => {
    if (t === '1' || t === 'h' || t === 'home') return 'home';
    if (t === 'x' || t === 'd' || t === 'draw') return 'draw';
    if (t === '2' || t === 'a' || t === 'away') return 'away';
    return null;
  };

  let htPick: 'home' | 'draw' | 'away' | null = null;
  let ftPick: 'home' | 'draw' | 'away' | null = null;
  const slash = norm.split('/');
  if (slash.length === 2) {
    htPick = tokenToSide(slash[0]!);
    ftPick = tokenToSide(slash[1]!);
  } else if (norm.length === 2) {
    htPick = tokenToSide(norm[0]!);
    ftPick = tokenToSide(norm[1]!);
  }
  if (!htPick || !ftPick) return null;

  const htActual: 'home' | 'draw' | 'away' =
    htScore.home > htScore.away ? 'home' :
    htScore.home < htScore.away ? 'away' :
                                  'draw';
  const ftActual: 'home' | 'draw' | 'away' =
    score.home > score.away ? 'home' :
    score.home < score.away ? 'away' :
                              'draw';

  const adjustedMargin = (htPick === htActual && ftPick === ftActual) ? 1 : -1;
  const outcome = resolveOutcome(edge.outcome, adjustedMargin);

  const sideLabel = (s: 'home' | 'draw' | 'away') =>
    s === 'home' ? 'Home' : s === 'draw' ? 'Draw' : 'Away';
  const actualText = `HT ${sideLabel(htActual)} → FT ${sideLabel(ftActual)}`;
  const marginText =
    outcome === 'pushed' ? `pushed · ${actualText}` :
                           actualText;

  return { outcome, marginText };
}

function deriveOUVerdict(args: {
  edge: EdgeCall;
  score: MatchScore;
}): VerdictResult | null {
  const { edge, score } = args;

  // Parse 'Over 2.75' / 'Under 3.5'
  const m = edge.line.match(/^(Over|Under)\s+(\d+(\.\d+)?)\s*$/i);
  if (!m) return null;
  const direction = m[1]!.toLowerCase() as 'over' | 'under';
  const threshold = Number(m[2]);
  const total = score.home + score.away;
  const delta = total - threshold;

  // "Adjusted margin" for OU: how much the actual total beat the bet
  // direction. Positive = bet hit, negative = bet missed.
  const adjustedMargin = direction === 'over' ? delta : -delta;

  const outcome = resolveOutcome(edge.outcome, adjustedMargin);

  const marginText =
    outcome === 'pushed' ? `pushed at ${total} goals` :
    outcome === 'won'    ? `${total} goals · ${direction} ${threshold} hit` :
                           `${total} goals · ${direction} ${threshold} missed`;

  return { outcome, marginText };
}

function marginPhrase(diff: number): string {
  const n = Math.abs(diff);
  if (n === 0) return 'tied at full time';
  return `by ${n} goal${n === 1 ? '' : 's'}`;
}

// ── Component ─────────────────────────────────────────────────────

export function SettledRecapCard({ match, edge, personas, defaultStake = 100 }: Props) {
  if (!match.score) {
    // No final score yet — shouldn't reach here, but defensive.
    return null;
  }

  const verdict = edge
    ? deriveVerdict({
        edge,
        homeShortName: match.home.shortName,
        awayShortName: match.away.shortName,
        score: match.score,
        htScore: match.htScore,
        corners: match.corners,
        yellows: match.yellows,
        reds:    match.reds,
      })
    : null;

  const isNoCall = !edge;

  return (
    <div className="recap">
      {/* POST-MORTEM EYEBROW — declares the surface up-front so the user
          immediately reads it as "looking back at the call", not
          "another bet-rec card". Brief §5.1 own-the-verdict mode. */}
      <div className="recap__eyebrow">
        <span className="recap__eyebrow-tag">POST-MORTEM</span>
        <span className="recap__eyebrow-sub">How the call landed</span>
      </div>

      {/* HERO MATCHUP — final score replaces kickoff time */}
      <section className="recap__hero">
        <div className="recap__league">
          <span>{match.league.name}</span>
          <span className="recap__ft">FT</span>
        </div>
        <div className="recap__teams">
          <TeamPill team={match.home} side="home" />
          <div className="recap__score">
            <div className="recap__score-nums">
              {match.score.home} – {match.score.away}
            </div>
            <div className="recap__score-label">Final</div>
          </div>
          <TeamPill team={match.away} side="away" />
        </div>
      </section>

      {/* VERDICT BLOCK */}
      {isNoCall ? (
        <NoCallVerdict score={match.score} />
      ) : verdict ? (
        <VerdictBlock edge={edge!} verdict={verdict} />
      ) : (
        <UnsupportedMarketVerdict edge={edge!} />
      )}

      {/* HYPOTHETICAL P&L — only when there's a verdict + edge */}
      {edge && verdict && (
        <PnlBlock edge={edge} outcome={verdict.outcome} defaultStake={defaultStake} />
      )}

      {/* LOCKED RECORD — original reasoning preserved (§5.5) */}
      {edge && <LockedRecord edge={edge} />}

      {/* PERSONA POST-MORTEM (§5.6) */}
      {edge && verdict && (
        <PersonaPostMortem edge={edge} personas={personas} outcome={verdict.outcome} />
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────

function TeamPill({ team, side }: { team: Match['home']; side: 'home' | 'away' }) {
  return (
    <div className={`recap__team recap__team--${side}`}>
      <span className="recap__crest" style={{ background: 'var(--surface-tint)' }}>
        <img
          src={team.crestUrl}
          alt={team.name}
          onError={(e) => {
            const img = e.currentTarget;
            const parent = img.parentElement;
            if (parent) {
              parent.style.background = team.primaryColor;
              parent.innerHTML = `<span style="color:#fff;font-weight:800;font-size:14px">${team.shortName[0]}</span>`;
            }
          }}
        />
      </span>
      <div className="recap__team-name">{team.shortName}</div>
    </div>
  );
}

function VerdictBlock({ edge, verdict }: { edge: EdgeCall; verdict: VerdictResult }) {
  const isWon = verdict.outcome === 'won';
  const isLost = verdict.outcome === 'lost';
  return (
    <section className={`recap__verdict recap__verdict--${verdict.outcome}`}>
      <div className="recap__verdict-label">
        Sharp8's call
      </div>
      <div className="recap__verdict-line">
        {edge.line} @ {edge.price.toFixed(2)} · {edge.book}
      </div>
      <div className={`recap__verdict-outcome${isWon ? ' won' : isLost ? ' lost' : ''}`}>
        {isWon  && <>Won · {verdict.marginText}</>}
        {isLost && <>Lost · {verdict.marginText}</>}
        {!isWon && !isLost && <>Pushed · {verdict.marginText}</>}
      </div>
    </section>
  );
}

function NoCallVerdict({ score }: { score: MatchScore }) {
  return (
    <section className="recap__no-call">
      <div className="recap__verdict-label">No edge on this match</div>
      <div className="recap__no-call-body">
        Sharp8's engine sat out. Final result for reference: <b>{score.home}–{score.away}</b>.
      </div>
    </section>
  );
}

function UnsupportedMarketVerdict({ edge }: { edge: EdgeCall }) {
  return (
    <section className="recap__verdict">
      <div className="recap__verdict-label">Sharp8's call</div>
      <div className="recap__verdict-line">
        {edge.line} @ {edge.price.toFixed(2)} · {edge.book}
      </div>
      <div className="recap__verdict-outcome">
        Settlement pending for this market type
      </div>
    </section>
  );
}

function PnlBlock({ edge, outcome, defaultStake }: {
  edge: EdgeCall; outcome: 'won' | 'lost' | 'pushed'; defaultStake: number;
}) {
  // Hypothetical P&L — explicitly NOT user's actual bet (D32).
  // Computed as: if you had placed $defaultStake on the cited line
  // at the cited price, this is what you would have won/lost.
  let profit = 0;
  if (outcome === 'won')    profit = +(defaultStake * (edge.price - 1)).toFixed(2);
  if (outcome === 'lost')   profit = -defaultStake;
  if (outcome === 'pushed') profit = 0;

  const cls = outcome === 'won' ? 'green' : outcome === 'lost' ? 'red' : '';

  return (
    <section className="recap__pnl">
      <div className="recap__pnl-row">
        <span className="recap__pnl-label">
          Hypothetical at ${defaultStake} default stake
        </span>
        <span className={`recap__pnl-amount ${cls}`}>
          {profit >= 0 ? '+' : ''}${profit.toFixed(2)}
        </span>
      </div>
      <p className="recap__pnl-note">
        Sharp8's call returned {profit >= 0 ? `+$${profit.toFixed(2)}` : `−$${Math.abs(profit).toFixed(2)}`} at
        the cited line. Actual bet placement happens on {edge.book}; Sharp8 doesn't read your account.
      </p>
    </section>
  );
}

function LockedRecord({ edge }: { edge: EdgeCall }) {
  return (
    <section className="recap__record">
      <div className="recap__section-label">The locked record</div>
      <div className="recap__record-grid">
        <Field label="Line"           value={edge.line} />
        <Field label="Price"          value={edge.price.toFixed(2)} />
        <Field label="Book"           value={edge.book} />
        <Field label="Edge at call"   value={`+${(edge.ev * 100).toFixed(1)}% EV`} />
        <Field label="Verified at"    value={fmtTime(edge.verifiedAt)} />
        <Field label="Panel"          value={`${edge.panel.backing} of ${edge.panel.total} backing`} />
      </div>
      <p className="recap__record-body">{edge.reasoning.body}</p>
      {edge.reasoning.citations.length > 0 && (
        <div className="recap__record-cites">
          {edge.reasoning.citations.map((c, i) => (
            <span key={i} className="recap__cite">{c.source}</span>
          ))}
        </div>
      )}
    </section>
  );
}

function PersonaPostMortem({ edge, personas, outcome }: {
  edge: EdgeCall; personas: Persona[]; outcome: 'won' | 'lost' | 'pushed';
}) {
  // Attribute the post-mortem to Sharp8 House (mediator persona).
  // Future iteration will wire a real own-the-verdict reply via the
  // brain Debate path now that LLM_BASE_URL is the integration point.
  const house = personas.find((p) => p.key === 'sharp8-house')
             ?? { key: 'sharp8-house', name: 'Sharp8 House' } as Persona;
  const av = PERSONA_AVATARS[house.key] ?? PERSONA_AVATARS['sharp8-house']!;

  const dissent = edge.panel.dissent[0];

  let commentary: string;
  if (outcome === 'won') {
    commentary = dissent
      ? `Synthesis weighted the consensus side correctly — ${edge.panel.backing}-of-${edge.panel.total} backing held. ${capitalize(dissent.personaKey)}'s dissent on ${dissent.alternativeLine} would have missed.`
      : `Synthesis was clean — all six lanes converged on ${edge.line}. The data lined up; the call landed.`;
  } else if (outcome === 'lost') {
    commentary = dissent
      ? `The dissent was right. ${capitalize(dissent.personaKey)} read ${dissent.alternativeLine} better; their signal weighting on ${dissent.reasoning.toLowerCase().split('.')[0]} should have carried more weight in the synthesis.`
      : `The signal that broke: the data favoured ${edge.line} at recommendation, the result didn't follow. Worth re-weighting similar setups.`;
  } else {
    commentary = 'Pushed on the line — Sharp8\'s call neither won nor lost.';
  }

  return (
    <section className="recap__postmortem">
      <div className="recap__section-label">The post-mortem</div>
      <div className="recap__postmortem-row">
        <span
          className="recap__postmortem-avatar"
          style={{ background: av.bg, color: av.fg }}
        >
          {av.letter}
        </span>
        <div className="recap__postmortem-body">
          <div className="recap__postmortem-name">{house.name}</div>
          <div className="recap__postmortem-text">{commentary}</div>
        </div>
      </div>
    </section>
  );
}

// ── Helpers ───────────────────────────────────────────────────────

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="recap__field">
      <div className="recap__field-label">{label}</div>
      <div className="recap__field-value">{value}</div>
    </div>
  );
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm} UTC`;
}

function capitalize(s: string): string {
  if (s === 'sharp8-house') return 'Sharp8 House';
  return s.charAt(0).toUpperCase() + s.slice(1);
}
